import { Injectable } from '@angular/core';

/**
 * Determines what environment the angular app is deployed to based on url
 */
@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {
  // The value of the axway header to use in the test environment to specify which backend GAP environment to access
  private readonly AXWAY_BACKEND_HEADER = 'apps{env}.highmark.com';

  private readonly localhostEnvironment = 'localhost';
  private readonly devEnvironments = ['tenv2', 'tenv6'];
  private readonly qaEnvironments = ['tenvb', 'tenv7', 'tenv3', 'tenvc', 'tenv1', 'tenve'];
  private readonly prodEnvironment = 'www';
  private readonly PROD_PREFIX =  'https://apiweb.hmhs.com';

  private readonly urlMap = {
    tenv2: 'intdev', 
    tenv6: 'intdev', 
    tenvb : 'qa', 
    tenv7: 'qa', 
    tenv3: 'qa',
    tenvc: 'qa', 
    tenv1: 'qa',  
    tenve: 'qa'
  };
  
  private readonly UCD_URL_PATTERN = 'https://(.*).unitedconcordia.com(.*)';
  private readonly SERVER_PATTERN = 'https://api{env}.hmhs.com';

  constructor() { }

  getServer() {
    if (this.isLocalhost()) {
      return '';
    } else if (this.isProd()) {
      return this.PROD_PREFIX;
    } else {
      const servUrl: string = this.urlMap[this.getEnvironment()];
      return servUrl ? 
        this.SERVER_PATTERN.replace('{env}', this.urlMap[this.getEnvironment()]) :
        this.SERVER_PATTERN.replace('{env}', this.urlMap[this.devEnvironments[0]]);
    }
  }

  getEnvironment(): string {
    if (this.isLocalhost()) {
      return 'tenv2';
    } else {
      const url = this.getUrl();
      return this.extractEnvironmentFromUrl(url);
    }
  }

  /**
   * Gets the current url
   */
  getUrl() {
    return window.location.href;
  }

  /**
   * Developing in a localhost environment
   */
  isLocalhost() {
    return this.getUrl().includes(this.localhostEnvironment);
  }

  /**
   * Development/IT environment
   */
  isDev() {
    return this.devEnvironments.some(env => this.getUrl().includes(env));
  }

  /**
   * QA environments (For axway purposes QA is the same as UAT)
   */
  isQa() {
    return this.qaEnvironments.some(env => this.getUrl().includes(env));
  }

  /**
   * Production environment
   */
  isProd() {
    return this.getUrl().includes(this.prodEnvironment);
  }

  /**
   * Extracts the environment name from the URL pattern
   */
  private extractEnvironmentFromUrl(url: string): string {
    const testMatch = url.match(this.UCD_URL_PATTERN);

    if (testMatch) {
        return testMatch[1];
    } else {
      return null;
    }
  }

  getAxwayBackendHeader(): string {
    const environment = this.getEnvironment();

    if (environment) {
      return this.AXWAY_BACKEND_HEADER.replace('{env}', environment);
    } else {
      return null;
    }
  }
}
